/**
 * View Club - Platform admin view for club details and management
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../services/api';
import { Button, FormTextArea, AdminPageLayout, StatCard, StatGrid, Section, Card, CardHeader, CardTitle, CardContent, Grid, Col, ConfirmDialog, Badge } from '../../components';
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Globe,
  Activity,
  Calendar,
  Users,
  Trophy,
  ShieldCheck,
  AlertCircle,
} from 'lucide-react';
import {
  AttributeGrid,
  AttributeStack,
  PropertyList,
  StatusNode,
} from '../../components/admin/patterns';

const ViewClub = () => {
  const navigate = useNavigate();
  const { id: club_id } = useParams();

  const [club, setClub] = useState(null);
  const [domains, setDomains] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmReactivate, setConfirmReactivate] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadClubData();
  }, [club_id]);

  const loadClubData = async () => {
    try {
      setLoading(true);
      const [clubData, domainData] = await Promise.all([
        apiService.getClub(club_id),
        apiService.getClubDomains(club_id, true).catch(err => {
          console.warn('Could not load domain data:', err);
          return null;
        })
      ]);
      setClub(clubData);
      setDomains(domainData);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to load club data.');
      setLoading(false);
    }
  };

  const handleDeactivateClub = async () => {
    try {
      setActionLoading(true);
      await apiService.deactivateClub(club_id, deactivateReason);
      setConfirmDeactivate(false);
      setDeactivateReason('');
      await loadClubData();
    } catch (err) {
      alert(`Error deactivating club: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateClub = async () => {
    try {
      setActionLoading(true);
      await apiService.reactivateClub(club_id);
      setConfirmReactivate(false);
      await loadClubData();
    } catch (err) {
      alert(`Error reactivating club: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getDomainStatusVariant = (domain) => {
    const status = domain.status;
    if (!status) return 'secondary';
    if (status.ready && status.ssl_provisioned) return 'success';
    if (status.ready && !status.ssl_provisioned) return 'warning';
    if (status.status === 'provisioning') return 'primary';
    return 'danger';
  };

  const getDomainStatusText = (domain) => {
    const status = domain.status;
    if (!status) return 'Unknown';
    if (status.ready && status.ssl_provisioned) return 'Active & Secure';
    if (status.ready && !status.ssl_provisioned) return 'Securing (SSL)';
    if (status.status === 'provisioning') return 'Provisioning';
    return 'Configuration Issue';
  };

  if (loading) {
    return (
      <AdminPageLayout title="LOADING CLUB..." maxWidth="1400px">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px', gap: '1.5rem' }}>
          <div className="loading-spinner"></div>
          <p style={{ opacity: 0.6 }}>Synchronizing organization data...</p>
        </div>
      </AdminPageLayout>
    );
  }

  if (error || !club) {
    return (
      <AdminPageLayout title="ERROR" maxWidth="1400px">
        <Card level={1} variant="feature" style={{ padding: '4rem', textAlign: 'center' }}>
          <AlertCircle size={48} color="var(--color-error)" style={{ marginBottom: '1.5rem' }} />
          <h2 style={{ marginBottom: '1rem' }}>Organization Not Found</h2>
          <p style={{ opacity: 0.7, marginBottom: '2rem' }}>{error || 'The requested club organization could not be retrieved.'}</p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <Button onClick={() => navigate('/admin/clubs')} variant="outline">Back to Clubs</Button>
            <Button onClick={loadClubData} variant="primary">Retry Connection</Button>
          </div>
        </Card>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="SCRUTINIZE ENTITY"
      subtitle={club.legal_name || club.display_name}
      backPath="/admin/clubs"
      maxWidth="1400px"
      actions={
        <div style={{ display: 'flex', gap: '1rem' }}>
          {club.status === 'active' ? (
            <Button onClick={() => setConfirmDeactivate(true)} variant="danger">Deactivate</Button>
          ) : (
            <Button onClick={() => setConfirmReactivate(true)} variant="success">Activate</Button>
          )}
          <Button onClick={() => navigate(`/admin/clubs/${club.id}/edit`)} variant="edit">Edit Club</Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3.5rem' }}>

        {/* 1. Statistics Summary */}
        <Section title="Statistics">
          <StatGrid>
            <StatCard number={club.total_players || 0} label="AUTHORIZED PARTICIPANTS" icon={<Users size={24} />} detail="Active registration total" />
            <StatCard number={club.total_coaches || 0} label="COACHING STAFF" icon={<Trophy size={24} />} variant="primary" />
            <StatCard number={club.total_parents || 0} label="MEMBER FAMILIES" icon={<Users size={24} />} variant="success" />
            <StatCard number={club.total_programs || 0} label="ACTIVE PROGRAMS" icon={<Activity size={24} />} variant="info" />
          </StatGrid>
        </Section>

        <Grid columns={12} gap="2.5rem">
          {/* 2. Core Identity */}
          <Col span={8}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3.5rem' }}>
              <Section title="ECOSYSTEM IDENTITY">
                <Card level={1}>
                  <CardContent>
                    <AttributeGrid columns={2} gap="3.5rem">
                      <AttributeStack label="Legal Entity" value={club.legal_name} variant="large" />
                      <AttributeStack
                        label="Governance Model"
                        value={<Badge variant="outline">{club.club_type?.replace('_', ' ').toUpperCase() || 'SOCCER CLUB'}</Badge>}
                      />
                      <AttributeStack label="Platform Endpoint" value={`${club.slug}.${window.location.hostname}`} />
                      <AttributeStack
                        label="Operational Status"
                        value={<Badge variant={club.status === 'active' ? 'success' : 'danger'}>{club.status.toUpperCase()}</Badge>}
                      />
                    </AttributeGrid>
                    {club.description && (
                      <div style={{ marginTop: '3.5rem', paddingTop: '2.5rem', borderTop: '1px solid var(--color-border-light)' }}>
                        <AttributeStack
                          label="Mission & Strategic Rationale"
                          value={club.description}
                          style={{ maxWidth: '800px' }}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Section>

              <Section title="LOCALIZATION & CONNECTIVITY">
                <Grid columns={2} gap="2.5rem">
                  <Card level={1}>
                    <CardHeader>
                      <CardTitle as="h4" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <Mail size={20} /> COMMUNICATIONS
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <AttributeGrid columns={1} gap="1.5rem">
                        <AttributeStack
                          label="Official Email"
                          value={<a href={`mailto:${club.contact_email}`} style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{club.contact_email}</a>}
                          icon={<Mail size={16} />}
                        />
                        <AttributeStack label="Administrative Hotline" value={club.contact_phone || 'STATION UNKNOWN'} icon={<Phone size={16} />} />
                        {club.website && (
                          <AttributeStack
                            label="Official Portal"
                            value={<a href={club.website} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>GO TO WEBSITE</a>}
                            icon={<Globe size={16} />}
                          />
                        )}
                      </AttributeGrid>
                    </CardContent>
                  </Card>

                  <Card level={1}>
                    <CardHeader>
                      <CardTitle as="h4" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <MapPin size={20} /> HEADQUARTERS
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div style={{ fontSize: '1rem', opacity: 0.8, lineHeight: '1.7', fontWeight: 500 }}>
                        {club.address_line1}<br />
                        {club.address_line2 && <>{club.address_line2}<br /></>}
                        {club.city}, {club.state} {club.zip_code}<br />
                        {club.country || 'UNITED STATES'}
                      </div>
                    </CardContent>
                  </Card>
                </Grid>
              </Section>

              {/* Domain Infrastructure */}
              {domains && (
                <Section title="TECHNICAL INFRASTRUCTURE">
                  <Card level={1}>
                    <CardHeader>
                      <CardTitle as="h3" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <ShieldCheck size={22} /> NETWORK ACCESS NODES
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {domains.automatic_subdomain && (
                          <StatusNode
                            title="Primary Platform Gateway"
                            status={getDomainStatusText(domains.automatic_subdomain).toUpperCase()}
                            badgeVariant={getDomainStatusVariant(domains.automatic_subdomain)}
                            description={domains.automatic_subdomain.domain}
                            icon={<Globe size={24} />}
                          />
                        )}

                        {domains.custom_domains?.map(domain => (
                          <StatusNode
                            key={domain.id}
                            title="Custom Organization Endpoint"
                            status={getDomainStatusText(domain).toUpperCase()}
                            badgeVariant={getDomainStatusVariant(domain)}
                            description={domain.domain}
                            icon={<ShieldCheck size={24} />}
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </Section>
              )}
            </div>
          </Col>

          {/* 3. Sidebar Details */}
          <Col span={4}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
              <Section title="PROTOCOL">
                <Card level={1}>
                  <CardHeader>
                    <CardTitle as="h4" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <ShieldCheck size={20} /> GOVERNANCE
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PropertyList items={[
                      { label: 'Registry Identifier', value: <code>{club.tenant_id}</code> },
                      { label: 'Primary Guardian ID', value: <code>{club.admin_user_id || 'NOT ASSIGNED'}</code> },
                      { label: 'Initialized', value: new Date(club.created_at).toLocaleDateString() },
                      { label: 'Last Sync', value: new Date(club.updated_at).toLocaleDateString() }
                    ]} />
                  </CardContent>
                </Card>
              </Section>

              <Section title="TREASURY">
                <Card level={1}>
                  <CardHeader>
                    <CardTitle as="h4" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <CreditCard size={20} /> BILLING RULES
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PropertyList items={[
                      { label: 'Method', value: club.preferred_payment_method?.replace('_', ' ').toUpperCase() || 'BANK TRANSFER' },
                      { label: 'Terms', value: `NET ${club.payment_terms || 30} DAYS` },
                      { label: 'Currency', value: <Badge variant="outline" size="sm" fontWeight={700}>{club.currency || 'USD'}</Badge> }
                    ]} />
                  </CardContent>
                </Card>
              </Section>

              <Section title="SYSTEM">
                <Card level={1}>
                  <CardContent>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', opacity: 0.8 }}>
                      <Calendar size={20} />
                      <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        {club.timezone || 'USA / NEW YORK'}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Section>
            </div>
          </Col>
        </Grid>
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        isOpen={confirmDeactivate}
        onClose={() => setConfirmDeactivate(false)}
        onConfirm={handleDeactivateClub}
        title="DEACTIVATE ORGANIZATION"
        variant="danger"
        loading={actionLoading}
        confirmText="Confirm Suspension"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <p style={{ margin: 0, lineHeight: '1.6' }}>
            Are you sure you want to suspend <strong style={{ color: 'var(--color-danger)' }}>{club.legal_name}</strong>? This will terminate all active sessions and revoke ecosystem access for all participants immediately.
          </p>
          <FormTextArea
            label="Operational Rationale for Suspension"
            value={deactivateReason}
            onChange={(e) => setDeactivateReason(e.target.value)}
            placeholder="Document the administrative reasoning for this intervention..."
            required
            rows={4}
          />
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={confirmReactivate}
        onClose={() => setConfirmReactivate(false)}
        onConfirm={handleReactivateClub}
        title="RESTORE ORGANIZATION"
        variant="success"
        loading={actionLoading}
        confirmText="Authorize Reactivation"
      >
        <p style={{ margin: 0, lineHeight: '1.6' }}>
          Do you wish to restore operational status for <strong>{club.legal_name}</strong>? This will immediately re-enable access for all registered ecosystem participants.
        </p>
      </ConfirmDialog>

    </AdminPageLayout>
  );
};

export default ViewClub;